'use client';

import {
	fadeTransition,
	fadeVariants,
	scaleSpringTransition,
	scaleSpringVariants,
	scaleTransition,
	scaleVariants,
	slideDownTransition,
	slideDownVariants,
	slideLeftTransition,
	slideLeftVariants,
	slideRightTransition,
	slideRightVariants,
	slideUpTransition,
	slideUpVariants,
} from '@/@shared/animations';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';

interface AnimatedConditionalProps {
	/** 컴포넌트를 표시할 조건 */
	show: boolean;
	/** 애니메이션할 자식 컴포넌트 */
	children: React.ReactNode;
	/** 애니메이션 타입 */
	animation?: 'fade' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'scale' | 'scale-spring';
	/** 애니메이션 지속시간 (초) */
	duration?: number;
	/** 지연시간 (초) */
	delay?: number;
	/** 커스텀 클래스명 */
	className?: string;
	/** 애니메이션 완료 후 콜백 */
	onAnimationComplete?: () => void;
	style?: React.CSSProperties;
}

// 애니메이션 프리셋 - animations.ts의 상수 사용
const animationPresets = {
	fade: fadeVariants,
	'slide-up': slideUpVariants,
	'slide-down': slideDownVariants,
	'slide-left': slideLeftVariants,
	'slide-right': slideRightVariants,
	scale: scaleVariants,
	'scale-spring': scaleSpringVariants,
};

const baseTransitions = {
	fade: fadeTransition,
	'slide-up': slideUpTransition,
	'slide-down': slideDownTransition,
	'slide-left': slideLeftTransition,
	'slide-right': slideRightTransition,
	scale: scaleTransition,
	'scale-spring': scaleSpringTransition,
};

const AnimatedConditional: React.FC<AnimatedConditionalProps> = ({
	show,
	children,
	animation = 'fade',
	duration,
	delay,
	className,
	onAnimationComplete,
	style,
}) => {
	const preset = animationPresets[animation];
	const baseTransition = baseTransitions[animation];

	// baseTransition을 기반으로 커스텀 duration과 delay 적용
	const transition = {
		...baseTransition,
		...(duration !== undefined && { duration }),
		...(delay !== undefined && { delay }),
	};

	return (
		<AnimatePresence mode="wait" onExitComplete={onAnimationComplete}>
			{show && (
				<motion.div
					className={className}
					variants={preset}
					initial="initial"
					animate="animate"
					exit="exit"
					transition={transition}
					style={style}
				>
					{children}
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default AnimatedConditional;
